import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message } from 'antd';
import { getNewToken } from './utils/auth';
import { request } from '@umijs/max';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

interface ErrorDetail {
  code: number;
  message: string;
}

// 与后端约定的响应数据格式 (对齐 Rust 的 ApiResponse)
interface ResponseStructure {
  success: boolean;
  data?: any;
  error?: ErrorDetail;
}

/**
 * @name 错误处理
 * 结合后端 Axum AppError 结构的统一错误处理
 */
export const errorConfig: RequestConfig = {
  errorConfig: {
    // 错误抛出：处理 HTTP 200 但 success: false 的情况
    errorThrower: (res) => {
      const { success, data, error: errorDetail } = res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorDetail?.message);
        error.name = 'BizError';
        error.info = {
          errorCode: errorDetail?.code,
          errorMessage: errorDetail?.message,
          // 🔧 FIX：200001 语义上应为静默错误
          showType:
            errorDetail?.code === 200001
              ? ErrorShowType.SILENT
              : ErrorShowType.ERROR_MESSAGE,
          data,
        };
        throw error;
      }
    },

    // 错误接收及处理：处理 BizError 以及 非 200 的 HTTP 错误
    errorHandler: async (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // 提取错误码
      const errorCode =
        error.info?.errorCode ||
        error.response?.data?.error?.code ||
        error.code;

      const requestId = error.response?.headers?.['x-request-id'];

      // --- [第一层：静默修复层] ---
      // 1. 处理 Token 过期自动刷新 (200001 为后端定义的 Auth 错误码)
      if (errorCode === 200001 && !opts?.url?.includes('/user/login')) {
        const newToken = await getNewToken();
        if (newToken) {
          const headers = {
            ...(opts.headers || {}),
            Authorization: `Bearer ${newToken}`,
          };
          // 🔧 FIX：重试请求必须跳过 errorHandler，彻底吞掉 200001
          return request(opts.url, {
            ...opts,
            headers,
            skipErrorHandler: true,
          });
        }
      }

      // --- [第二层：报错通知层] ---
      // 只有没被第一层 return 掉的错误，才会进入这里弹窗
      let errorMsg = '';

      // 2. 处理业务逻辑错误 (errorThrower 抛出的 BizError)
      if (error.name === 'BizError') {
        const { errorCode: code, errorMessage: msg } = error.info || {};
        errorMsg = code ? `[${code}] ${msg}` : msg;
      }
      // 3. 处理非 200 HTTP 错误 (如 400, 401, 500)
      else if (error.response) {
        const bizData = error.response.data as ResponseStructure;
        const bizMessage = bizData?.error?.message;
        const bizCode = bizData?.error?.code;

        const statusMsg = bizMessage
          ? (bizCode ? `[${bizCode}] ${bizMessage}` : bizMessage)
          : `系统请求异常 (Status: ${error.response.status})`;

        errorMsg = statusMsg;
      }
      // 4. 处理网络未响应
      else if (error.request) {
        errorMsg = '服务器未响应，请检查网络连接';
      }
      // 5. 其他请求配置错误
      else {
        errorMsg = '请求发起失败，请重试';
      }

      // 统一执行弹窗（带上 Request ID）
      if (errorMsg) {
        message.error(requestId ? `${errorMsg} (ID: ${requestId})` : errorMsg);
      }

      // 💡 必须抛出错误，以停止页面组件（如 ProForm）的 loading 状态
      throw error;
    },
  },

  // 请求拦截器：注入 Token
  requestInterceptors: [
    (config: RequestOptions) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        return {
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${token}`,
          },
        };
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      return response;
    },
  ],
};
